import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { ButtonForm, CustomTextField, LinkButton } from 'Components/Form/styles';
import { Column, Row } from 'Components/Grid';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssetsNames } from 'services/assets-service';
import { getDatas } from 'services/datas-service';
import {
  addProcessDataMapping,
  editProcessDataMapping,
  getProcessMappingByIdAndMappingId,
} from 'services/processes-service';
import { getLegalBases, getDataCategories } from 'services/mapping-service';
import { getSystemsNames } from 'services/systems-service';
import { useAuthTenant } from 'store/auth';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { PageLoaderForm } from 'Components/PageLoader/Form';
import { useEffect, useState } from 'react';
import { Container } from 'Components/Container';
import { FormSuccess } from 'Components/Form/Success';
import { treatments } from './helpers';
import { InputLoader } from 'Components/PageLoader/Input';

const TREATMENT_LABEL_KEY = {
  Criar: 'create',
  Armazenar: 'store',
  Acessar: 'access',
  Compartilhar: 'share',
  Arquivar: 'archive',
  Destruir: 'destroy',
};

const MAX_CUSTOM_TEXT_LENGTH = 500;

export const DataMapping = () => {
  const { t, i18n } = useTranslation(['mapping']);
  const { id, mappingId } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const selectedTenant = useSelector(useAuthTenant);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      category: '',
      customDataCategory: '',
      dataSource: '',
      holderProfile: '',
      retentionPeriod: '',
      storage: '',
      dataProcessing: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dataProcessing',
  });

  const handleAddDataProcessing = () => {
    append({
      processing: '',
      shareType: '',
      legalBase: '',
      customLegalBase: '',
      complementLegalBasis: '',
      finality: '',
      position: '',
      tool: '',
      observation: '',
      destiny: '',
    });
  };

  const formatForm = dados => {
    return {
      name: dados.name,
      category: dados.category,
      customDataCategory: dados.category === 'other' ? dados.customDataCategory : undefined,
      dataSource: dados.dataSource,
      holderProfile: dados.holderProfile,
      retentionPeriod: dados.retentionPeriod,
      storage: dados.storage,
      dataProcessing: dados.dataProcessing.length > 0
        ? dados.dataProcessing.map(dp => ({
            ...dp,
            customLegalBase: dp.legalBase === 'other' ? dp.customLegalBase : undefined,
          }))
        : [],
    };
  };

  const onSubmit = async () => {
    try {
      const formattedForm = formatForm(getValues());
      const res = mappingId
        ? await editProcessDataMapping(id, mappingId, formattedForm)
        : await addProcessDataMapping(id, formattedForm);

      if (res.status === 200) {
        setFinished(true);
      } else {
        throw new Error(t('mapping:dataMapping.errorDialog.defaultMessage'));
      }
    } catch (error) {
      SwalReact.fire({
        title: t('mapping:dataMapping.errorDialog.title'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    navigate(`/processes/show/${id}`);
  };

  const {
    data: legalBasesData,
    isLoading: legalBasesLoading,
    isError: legalBasesError,
  } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      const result = await getLegalBases(selectedTenant?.slug);
      return result?.data?.data ?? [];
    },
    {
      onError: (err) => console.error('[legal-bases] fetch failed:', err),
    },
  );

  const {
    data: dataCategoriesData,
    isLoading: dataCategoriesLoading,
    isError: dataCategoriesError,
  } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      const result = await getDataCategories(selectedTenant?.slug);
      return result?.data?.data ?? [];
    },
    {
      onError: (err) => console.error('[data-categories] fetch failed:', err),
    },
  );

  const queries = useQueries([
    {
      queryKey: ['getSystemsNames'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getSystemsNames(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getAssetsNames'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAssetsNames(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getDatas'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getDatas(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
  ]);

  const [
    { data: systems, isLoading: systemLoading, isError: systemError },
    { data: assets, isLoading: assetLoading, isError: assetError },
    { data: datas, isLoading: dataLoading, isError: dataError },
  ] = queries;

  const {
    isLoading,
    error,
    data: dataMapping,
  } = useQuery(['getProcessMapping', id, mappingId], async () => {
    if (!id) return null;
    if (!mappingId) return null;

    const resProcessMapping = await getProcessMappingByIdAndMappingId(id, mappingId);

    if (!resProcessMapping.data) return null;

    return resProcessMapping.data;
  });

  useEffect(() => {
    if (!id) return;
    if (!mappingId) return;
    if (!dataMapping) return;

    Object.keys(dataMapping).forEach(key => {
      setValue(key, dataMapping[key]);
    });

    return () => {};
  }, [id, mappingId, dataMapping, setValue]);
  const processing = watch('dataProcessing');

  return (
    <Container
      title={mappingId ? t('mapping:dataMapping.title.edit') : t('mapping:dataMapping.title.add')}
      breadcrumb={t('mapping:dataMapping.breadcrumb')}
      linkPage={`/processes/show/${id}`}
    >
      <div
        className="card-content"
        style={{
          width: '80%',
        }}
      >
        {!error && !isLoading && !finished && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>
              <Row>
                <Column className="mb-3 font-weight-bold">
                  {t('mapping:dataMapping.dataIndex', { index: mappingId ? mappingId + 1 : 1 })}
                </Column>
              </Row>
              <Row justifyContent="center">
                <Column xs={12} sm={6} flex>
                  {!dataLoading && !dataError && (
                    <Controller
                      control={control}
                      rules={{
                        validate: () => {
                          return getValues(`name`) !== '';
                        },
                        required: t('mapping:dataMapping.errors.required.data'),
                      }}
                      name={`name`}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <FormControl fullWidth sx={{ marginBottom: '24px' }} error={fieldState.error ? true : false}>
                          <InputLabel id={`name-label`} shrink>
                            {t('mapping:dataMapping.fields.data')}
                          </InputLabel>
                          <Select
                            labelId={`name-label`}
                            id={`name`}
                            value={value}
                            label={t('mapping:dataMapping.fields.data')}
                            onChange={onChange}
                            notched
                          >
                            <MenuItem value="" key={-1}>
                              {t('mapping:dataMapping.placeholders.data')}
                            </MenuItem>
                            {datas?.map(item => (
                              <MenuItem value={item.name} key={item._id}>
                                {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  )}
                  {dataLoading && !dataError && (
                    <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                      <InputLabel id="name-label" shrink={true}>
                        {t('mapping:dataMapping.fields.data')}
                      </InputLabel>
                      <InputLoader />
                    </FormControl>
                  )}
                </Column>
                <Column xs={12} sm={6} flex>
                  {!dataCategoriesLoading && (
                    <Controller
                      control={control}
                      name={`category`}
                      rules={{
                        validate: () => getValues('category') !== '',
                        required: t('mapping:dataMapping.errors.required.category'),
                      }}
                      render={({ field: { onChange, value }, fieldState }) => {
                        const options = !dataCategoriesError ? (dataCategoriesData ?? []) : [];
                        const knownKeys = new Set(options.map(item => item.key));
                        const isLegacyValue = value && !knownKeys.has(value);
                        const sortedOptions = [
                          ...options.filter(item => item.key !== 'other'),
                          ...options.filter(item => item.key === 'other'),
                        ];
                        return (
                          <>
                            <FormControl fullWidth sx={{ marginBottom: '24px' }} error={fieldState.error ? true : false}>
                              <InputLabel id={`category-label`} shrink>
                                {t('mapping:data_categories.label')}
                              </InputLabel>
                              <Select
                                labelId={`category-label`}
                                id={`category`}
                                value={value}
                                label={t('mapping:data_categories.label')}
                                onChange={onChange}
                                notched
                                disabled={dataCategoriesError}
                              >
                                <MenuItem value="" key="">
                                  {dataCategoriesError
                                    ? t('mapping:data_categories.error')
                                    : t('mapping:data_categories.placeholder')}
                                </MenuItem>
                                {isLegacyValue && (
                                  <MenuItem value={value} key="legacy">
                                    {value}
                                  </MenuItem>
                                )}
                                {sortedOptions.map(item => (
                                  <MenuItem value={item.key} key={item.key}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                            </FormControl>
                            {watch('category') === 'other' && (
                              <CustomTextField
                                {...register('customDataCategory', {
                                  required: t('mapping:data_categories.custom_required'),
                                  validate: v => (!v?.trim() ? t('mapping:data_categories.custom_required') : true),
                                  maxLength: {
                                    value: MAX_CUSTOM_TEXT_LENGTH,
                                    message: t('mapping:data_categories.custom_max_length', { count: MAX_CUSTOM_TEXT_LENGTH }),
                                  },
                                })}
                                label={t('mapping:data_categories.custom_label')}
                                placeholder={t('mapping:data_categories.custom_placeholder')}
                                error={!!errors?.customDataCategory}
                                helperText={errors?.customDataCategory?.message ?? null}
                                inputProps={{ maxLength: MAX_CUSTOM_TEXT_LENGTH }}
                                sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          </>
                        );
                      }}
                    />
                  )}
                  {dataCategoriesLoading && (
                    <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                      <InputLabel id="category-label" shrink={true}>
                        {t('mapping:data_categories.label')}
                      </InputLabel>
                      <InputLoader />
                    </FormControl>
                  )}
                </Column>
              </Row>
              <Row justifyContent="center">
                <Column xs={12} sm={6} flex>
                  <CustomTextField
                    {...register(`dataSource`, { required: t('mapping:dataMapping.errors.required.dataSource') })}
                    label={t('mapping:dataMapping.fields.dataSource')}
                    autoFocus
                    error={errors?.dataSource ? true : false}
                    sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                    helperText={errors?.dataSource ? errors.dataSource.message : null}
                    InputLabelProps={{ shrink: true }}
                  />
                </Column>
                <Column xs={12} sm={6} flex>
                  <CustomTextField
                    {...register(`holderProfile`, { required: t('mapping:dataMapping.errors.required.holderProfile') })}
                    label={t('mapping:dataMapping.fields.holderProfile')}
                    autoFocus
                    error={errors?.holderProfile ? true : false}
                    sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                    helperText={errors?.holderProfile ? errors.holderProfile.message : null}
                    InputLabelProps={{ shrink: true }}
                  />
                </Column>
              </Row>
              <Row justifyContent="center">
                <Column xs={12} sm={6} flex>
                  <CustomTextField
                    {...register(`retentionPeriod`)}
                    label={t('mapping:dataMapping.fields.retentionPeriod')}
                    autoFocus
                    sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Column>
                <Column xs={12} sm={6} flex>
                  {!systemLoading && !systemError && !assetLoading && !assetError && (
                    <Controller
                      control={control}
                      rules={{
                        validate: () => {
                          return getValues(`storage`) !== '';
                        },
                        required: t('mapping:dataMapping.errors.required.storage'),
                      }}
                      name={`storage`}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <FormControl fullWidth sx={{ marginBottom: '24px' }} error={fieldState.error ? true : false}>
                          <InputLabel id={`storage-label`} shrink>
                            {t('mapping:dataMapping.fields.storage')}
                          </InputLabel>
                          <Select
                            labelId={`storage-label`}
                            id={`storage`}
                            value={value}
                            label={t('mapping:dataMapping.fields.storage')}
                            onChange={onChange}
                            notched
                          >
                            <MenuItem value="" key={-1}>
                              {t('mapping:dataMapping.placeholders.storage')}
                            </MenuItem>
                            {systems?.map(item => (
                              <MenuItem value={item.name} key={item._id}>
                                {item.name}
                              </MenuItem>
                            ))}
                            {assets?.map(item => (
                              <MenuItem value={item.name} key={item._id}>
                                {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  )}
                  {(systemLoading || assetLoading) && !systemError && !assetError && (
                    <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                      <InputLabel id="storage-label" shrink={true}>
                        {t('mapping:dataMapping.fields.storage')}
                      </InputLabel>
                      <InputLoader />
                    </FormControl>
                  )}
                </Column>
              </Row>
              <Row>
                <Column>
                  {fields?.map((itemProcess, indexProcess) => {
                    return (
                      <div key={`${itemProcess.processing}-${indexProcess}`}>
                        <Row>
                          <Column className="mb-2">
                            <hr />
                          </Column>
                        </Row>
                        <Row>
                          <Column xs={6} className="mb-3">
                            <LinkButton
                              onClick={() => {
                                remove(indexProcess);
                              }}
                            >
                              {t('mapping:dataMapping.actions.removeTreatment')}
                            </LinkButton>
                          </Column>
                        </Row>
                        <Row>
                          <Column className="mb-3 font-weight-bold">
                            {t('mapping:dataMapping.treatmentIndex', { index: indexProcess + 1 })}
                          </Column>
                        </Row>
                        <Row justifyContent="center">
                          <Column xs={12} sm={6} flex>
                            <Controller
                              control={control}
                              name={`dataProcessing.${indexProcess}.processing`}
                              render={({ field: { onChange, value } }) => (
                                <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                                  <InputLabel id={`processing${indexProcess}-label`} shrink>
                                    {t('mapping:dataMapping.fields.treatment')}
                                  </InputLabel>
                                  <Select
                                    labelId={`processing${indexProcess}-label`}
                                    id={`processing${indexProcess}`}
                                    value={value ?? ''}
                                    label={t('mapping:dataMapping.fields.treatment')}
                                    onChange={onChange}
                                    notched
                                  >
                                    <MenuItem value="" key={-1}>
                                      {t('mapping:dataMapping.placeholders.treatment')}
                                    </MenuItem>
                                    {treatments.map(item => (
                                      <MenuItem value={item} key={item}>
                                        {TREATMENT_LABEL_KEY[item]
                                          ? t(`mapping:dataMapping.treatments.${TREATMENT_LABEL_KEY[item]}`)
                                          : item}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Column>
                          <Column xs={12} sm={6} flex>
                            {!legalBasesLoading && (
                              <Controller
                                control={control}
                                name={`dataProcessing.${indexProcess}.legalBase`}
                                render={({ field: { onChange, value } }) => {
                                  const legalBasesOptions = !legalBasesError ? (legalBasesData ?? []) : [];
                                  const sortedLegalBases = [
                                    ...legalBasesOptions.filter(item => item.key !== 'other'),
                                    ...legalBasesOptions.filter(item => item.key === 'other'),
                                  ];
                                  return (
                                    <>
                                      <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                                        <InputLabel id={`legalBase${indexProcess}-label`} shrink>
                                          {t('mapping:legal_bases.label')}
                                        </InputLabel>
                                        <Select
                                          labelId={`legalBase${indexProcess}-label`}
                                          id={`legalBase${indexProcess}`}
                                          value={value ?? ''}
                                          label={t('mapping:legal_bases.label')}
                                          onChange={onChange}
                                          notched
                                          disabled={legalBasesError}
                                        >
                                          <MenuItem value="" key="">
                                            {legalBasesError
                                              ? t('mapping:legal_bases.error')
                                              : t('mapping:legal_bases.placeholder')}
                                          </MenuItem>
                                          {sortedLegalBases.map(item => (
                                            <MenuItem value={item.key} key={item.key}>
                                              {item.name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      {watch(`dataProcessing.${indexProcess}.legalBase`) === 'other' && (
                                        <CustomTextField
                                          {...register(`dataProcessing.${indexProcess}.customLegalBase`, {
                                            required: t('mapping:legal_bases.custom_required'),
                                            validate: v => (!v?.trim() ? t('mapping:legal_bases.custom_required') : true),
                                            maxLength: {
                                              value: MAX_CUSTOM_TEXT_LENGTH,
                                              message: t('mapping:legal_bases.custom_max_length', { count: MAX_CUSTOM_TEXT_LENGTH }),
                                            },
                                          })}
                                          label={t('mapping:legal_bases.custom_label')}
                                          placeholder={t('mapping:legal_bases.custom_placeholder')}
                                          error={!!errors?.dataProcessing?.[indexProcess]?.customLegalBase}
                                          helperText={errors?.dataProcessing?.[indexProcess]?.customLegalBase?.message ?? null}
                                          inputProps={{ maxLength: MAX_CUSTOM_TEXT_LENGTH }}
                                          sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      )}
                                    </>
                                  );
                                }}
                              />
                            )}
                            {legalBasesLoading && (
                              <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                                <InputLabel id={`legalBase${indexProcess}-label`} shrink={true}>
                                  {t('mapping:legal_bases.label')}
                                </InputLabel>
                                <InputLoader />
                              </FormControl>
                            )}
                          </Column>
                        </Row>
                        {/* só exibir se o tratamentro for compartilhamento */}
                        {processing[indexProcess].processing === 'Compartilhar' && (
                          <Row justifyContent="center">
                            <Column xs={12} sm={6} flex>
                              <Controller
                                control={control}
                                name={`dataProcessing.${indexProcess}.shareType`}
                                render={({ field: { onChange, value } }) => (
                                  <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                                    <InputLabel id={`shareType${indexProcess}-label`} shrink>
                                      {t('mapping:dataMapping.fields.shareType')}
                                    </InputLabel>
                                    <Select
                                      labelId={`shareType${indexProcess}-label`}
                                      id={`shareType${indexProcess}`}
                                      value={value ?? ''}
                                      label={t('mapping:dataMapping.fields.shareType')}
                                      onChange={onChange}
                                      notched
                                    >
                                      <MenuItem value="" key={-1}>
                                        {t('mapping:dataMapping.placeholders.shareType')}
                                      </MenuItem>
                                      <MenuItem value="Interno" key="internal">
                                        {t('mapping:dataMapping.shareTypes.internal')}
                                      </MenuItem>
                                      <MenuItem value="Externo" key="external">
                                        {t('mapping:dataMapping.shareTypes.external')}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                )}
                              />
                            </Column>
                            <Column xs={12} sm={6} flex>
                              <CustomTextField
                                {...register(`dataProcessing.${indexProcess}.destiny`)}
                                label={t('mapping:dataMapping.fields.destiny')}
                                autoFocus
                                sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Column>
                          </Row>
                        )}
                        <Row justifyContent="center">
                          <Column xs={12} sm={12} flex>
                            <CustomTextField
                              {...register(`dataProcessing.${indexProcess}.complementLegalBasis`)}
                              label={t('mapping:dataMapping.fields.complementLegalBasis')}
                              autoFocus
                              sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Column>
                        </Row>
                        <Row justifyContent="center">
                          <Column xs={12} sm={6} flex>
                            <CustomTextField
                              {...register(`dataProcessing.${indexProcess}.finality`)}
                              label={t('mapping:dataMapping.fields.finality')}
                              sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Column>
                          <Column xs={12} sm={6} flex>
                            <Controller
                              control={control}
                              name={`dataProcessing.${indexProcess}.position`}
                              render={({ field: { onChange, value } }) => (
                                <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                                  <InputLabel id={`position${indexProcess}-label`} shrink>
                                    {t('mapping:dataMapping.fields.position')}
                                  </InputLabel>
                                  <Select
                                    labelId={`position${indexProcess}-label`}
                                    id={`position${indexProcess}`}
                                    value={value ?? ''}
                                    label={t('mapping:dataMapping.fields.position')}
                                    onChange={onChange}
                                    notched
                                  >
                                    <MenuItem value="" key={-1}>
                                      {t('mapping:dataMapping.placeholders.position')}
                                    </MenuItem>
                                    <MenuItem value="Controlador" key="controller">
                                      {t('mapping:dataMapping.positions.controller')}
                                    </MenuItem>
                                    <MenuItem value="Operador" key="operator">
                                      {t('mapping:dataMapping.positions.operator')}
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Column>
                        </Row>
                        <Row>
                          <Column xs={12} sm={12} flex>
                            {!systemLoading && !systemError && !assetLoading && !assetError && (
                              <Controller
                                control={control}
                                name={`dataProcessing.${indexProcess}.tool`}
                                render={({ field: { onChange, value } }) => (
                                  <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                                    <InputLabel id={`tool${indexProcess}-label`} shrink>
                                      {t('mapping:dataMapping.fields.tool')}
                                    </InputLabel>
                                    <Select
                                      labelId={`tool${indexProcess}-label`}
                                      id={`tool${indexProcess}`}
                                      value={value ?? ''}
                                      label={t('mapping:dataMapping.fields.tool')}
                                      onChange={onChange}
                                      notched
                                    >
                                      <MenuItem value="" key={-1}>
                                        {t('mapping:dataMapping.placeholders.tool')}
                                      </MenuItem>
                                      {systems?.map(item => (
                                        <MenuItem value={item.name} key={item._id}>
                                          {item.name}
                                        </MenuItem>
                                      ))}
                                      {assets?.map(item => (
                                        <MenuItem value={item.name} key={item._id}>
                                          {item.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              />
                            )}
                            {(systemLoading || assetLoading) && !systemError && !assetError && (
                              <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                                <InputLabel id={`tool${indexProcess}-label`} shrink={true}>
                                  {t('mapping:dataMapping.fields.tool')}
                                </InputLabel>
                                <InputLoader />
                              </FormControl>
                            )}
                          </Column>
                        </Row>
                        <Row>
                          <Column>
                            <TextField
                              {...register(`dataProcessing.${indexProcess}.observation`)}
                              label={t('mapping:dataMapping.fields.observation')}
                              multiline
                              rows={4}
                              sx={{ margin: '0 auto 24px', width: '100%' }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Column>
                        </Row>
                      </div>
                    );
                  })}
                </Column>
              </Row>
              <Row>
                <Column className="mb-3">
                  <LinkButton onClick={handleAddDataProcessing}>
                    {t('mapping:dataMapping.actions.addTreatment')}
                  </LinkButton>
                </Column>
              </Row>
              <Row>
                <Column className="mb-2">
                  <hr />
                </Column>
              </Row>
            </div>

            <Row justifyContent="end" alignItems="center" className="mt-2">
              <Column xs={3} justifyContent="center" className="d-flex">
                <ButtonForm secondary id="cancel" label={t('mapping:dataMapping.actions.cancel')} onClick={handleReset}></ButtonForm>
              </Column>
              <Column xs={2} justifyContent="center" className="d-flex">
                <ButtonForm label={t('mapping:dataMapping.actions.save')} type="submit" />
              </Column>
            </Row>
          </form>
        )}
        {isLoading && <PageLoaderForm />}

        {finished && (
          <FormSuccess
            title={t('mapping:dataMapping.success.title')}
            labelStartAgain={t('mapping:dataMapping.success.addAnother')}
            handleStartAgain={() => handleReset()}
            labelShow={t('mapping:dataMapping.success.viewDetails')}
            handleShow={() => {
              navigate(`/processes/show/${id}`);
            }}
          />
        )}
      </div>
    </Container>
  );
};
